import { createMemo, createSignal, For, Show, type JSX } from 'solid-js';
import { BusyButton, CardSection, ConfirmModal } from '../components/ui';
import { can } from '../lib/permissions.generated';
import type { EffectPackageParameterValue } from '../lib/effectPackages';
import type { EffectPreset } from '../lib/effectPresets';
import type { EffectStack } from '../lib/effectStack';
import { useVenue } from '../state/VenueProvider';
import { testIdOf } from '../testIds';

interface EffectPresetsProps {
  /** The package whose library this is. A preset belongs to one effect, never to the room at large. */
  packageId: string;
  idPrefix: string;
  /** What the card is showing right now — the values a save captures. */
  values: () => Record<string, EffectPackageParameterValue>;
  /**
   * The rest of what the card is showing, for the one effect whose look is more than knob positions:
   * audio reactive's whole layer stack. A package card passes neither.
   */
  stack?: () => EffectStack | undefined;
  /** Where an applied preset goes: back through the card's single write path. */
  onApply: (preset: EffectPreset) => void;
}

/**
 * The room's named looks for one effect.
 *
 * The room already REMEMBERS the values it was last run with — that is its working copy, held on the
 * controller's own disk and recoverable by re-tuning. A preset is the other thing: a look somebody
 * authored and wants back, which is not recoverable, so it lives with the venue and follows the room
 * rather than the SD card currently in it.
 *
 * Applying one does not open a second route into the room. It hands the values to the card, which sends
 * them exactly the way a slider drag does: a parameter patch while the effect runs, and the next start's
 * patch while it is stopped. One write path, one set of rules about held keys and refusals.
 *
 * Renaming happens in the row itself rather than in a dialog. A dialog would need a title, and a title
 * here is a word nobody asked for over a field whose one button already says what it does: the button
 * that opened the edit is the button that commits it, and reads `Accept` while it is open (owner,
 * 2026-09-16). Enter commits it too, and Escape leaves the name as it was.
 */
export function EffectPresets(props: EffectPresetsProps): JSX.Element {
  const venue = useVenue();
  const [name, setName] = createSignal('');
  const [renamingId, setRenamingId] = createSignal('');
  const [renamed, setRenamed] = createSignal('');
  const [deleting, setDeleting] = createSignal<EffectPreset>();

  // Present or absent, never present-and-dead: an operator may run a saved look but may not author one,
  // so they are not shown the controls the cloud would refuse them.
  const canManage = createMemo(() => can(venue.role(), 'effects.packages.manage'));
  const presets = createMemo(() => venue.effectPresets().filter((preset) => preset.packageId === props.packageId));
  const busy = (): boolean => Boolean(venue.actionBusy());

  async function save(event: Event): Promise<void> {
    event.preventDefault();
    const label = name().trim();
    if (!label) return;
    const stack = props.stack?.();
    const saved = await venue.runAction(
      'save-effect-preset',
      () => venue.saveEffectPreset(props.packageId, label, props.values(), {
        ...(stack ? { stack } : {}),
      }),
    );
    // The new row landing in the list below is the confirmation; there is nothing to announce.
    if (saved) setName('');
  }

  async function apply(preset: EffectPreset): Promise<void> {
    const applied = await venue.runAction(
      'apply-effect-preset',
      () => venue.applyEffectPreset(preset.presetId),
    );
    if (applied) props.onApply(applied);
  }

  async function rename(preset: EffectPreset): Promise<void> {
    if (renamingId() !== preset.presetId) {
      setRenamed(preset.name);
      setRenamingId(preset.presetId);
      return;
    }
    const label = renamed().trim();
    if (!label || label === preset.name) {
      setRenamingId('');
      return;
    }
    const result = await venue.runAction(
      'rename-effect-preset',
      () => venue.renameEffectPreset(preset.presetId, label),
    );
    if (result) setRenamingId('');
  }

  async function commitDelete(): Promise<void> {
    const preset = deleting();
    if (!preset) return;
    const result = await venue.runAction(
      'delete-effect-preset',
      async () => { await venue.deleteEffectPreset(preset.presetId); return true; },
    );
    if (result) setDeleting(undefined);
  }

  return (
    <>
      {/* A library nobody may add to and that holds nothing is not a section — it is an empty box with a
          word over it. It appears when there is something in it, or when the person looking can fill it. */}
      <Show when={canManage() || presets().length > 0}>
        <CardSection
          class="effect-preset-section"
          testId={testIdOf('presets', { prefix: props.idPrefix })}
          bodyClass="effect-preset-list"
          bodyTestId={testIdOf('presetList', { prefix: props.idPrefix })}
          heading={<h3 class="effect-preset-heading">Presets</h3>}
          tools={(
            <Show when={canManage()}>
              <form class="effect-preset-add" onSubmit={(event) => void save(event)}>
                <input
                  type="text"
                  value={name()}
                  onInput={(event) => setName(event.currentTarget.value)}
                  aria-label="Preset name"
                  autocomplete="off"
                  maxlength="48"
                  data-testid={testIdOf('presetName', { prefix: props.idPrefix })}
                />
                <BusyButton
                  busyKey="save-effect-preset"
                  type="submit"
                  disabled={!name().trim() || !venue.controlsEnabled()}
                  data-testid={testIdOf('presetSave', { prefix: props.idPrefix })}
                >Save preset</BusyButton>
              </form>
            </Show>
          )}
        >
          <For each={presets()}>
            {(preset) => (
              <div data-testid={testIdOf('preset', { prefix: props.idPrefix, preset: preset.presetId })}>
                <Show
                  when={renamingId() === preset.presetId}
                  fallback={<strong>{preset.name}</strong>}
                >
                  <input
                    type="text"
                    value={renamed()}
                    onInput={(event) => setRenamed(event.currentTarget.value)}
                    onKeyDown={(event) => {
                      if (event.key === 'Escape') setRenamingId('');
                      if (event.key === 'Enter') { event.preventDefault(); void rename(preset); }
                    }}
                    aria-label="Preset name"
                    autocomplete="off"
                    maxlength="48"
                    ref={(element) => queueMicrotask(() => element.focus())}
                    data-testid={testIdOf('presetRenameName', { prefix: props.idPrefix })}
                  />
                </Show>
                <button
                  type="button"
                  class="text-button"
                  disabled={!venue.controlsEnabled() || busy() || renamingId() === preset.presetId}
                  onClick={() => void apply(preset)}
                  data-testid={testIdOf('presetApply', { prefix: props.idPrefix, preset: preset.presetId })}
                >Apply</button>
                <Show when={canManage()}>
                  <button
                    type="button"
                    class="text-button"
                    disabled={busy()}
                    onClick={() => void rename(preset)}
                    data-testid={testIdOf('presetRename', { prefix: props.idPrefix, preset: preset.presetId })}
                  >{renamingId() === preset.presetId ? 'Accept' : 'Rename'}</button>
                  <button
                    type="button"
                    class="text-button danger-text"
                    disabled={busy()}
                    onClick={() => setDeleting(preset)}
                    data-testid={testIdOf('presetDelete', { prefix: props.idPrefix, preset: preset.presetId })}
                  >Delete</button>
                </Show>
              </div>
            )}
          </For>
        </CardSection>
      </Show>

      {/* Deleting a saved look is not recoverable by re-tuning, which is the whole reason presets exist
          off the controller's disk — so it asks first, like every other destructive action here. */}
      <ConfirmModal
        open={Boolean(deleting())}
        title="Delete preset?"
        detail={deleting()?.name}
        confirmLabel="Delete"
        tone="danger"
        busyKey="delete-effect-preset"
        onConfirm={() => void commitDelete()}
        onCancel={() => setDeleting(undefined)}
      />
    </>
  );
}
